<script setup lang="ts">import { computed as _computed, ref as _ref } from 'vue';

import { all as allowedCountries } from '@consumer/enums/AllowedCountries'
import countries from '@corp/enums/countries'
import type { Phone } from '@consumer/utils/phoneNumber'
import { parsePhone } from '@consumer/utils/phoneNumber'
import { faChevronDown, faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons'
import { computePosition, flip, offset } from '@floating-ui/dom'
import { onClickOutside, useCurrentElement, useResizeObserver } from '@vueuse/core'
import { scrollIntoView } from '@shared/scroll'

const defaultCountryCode = '1'

const allowedCountryCodes = allowedCountries.map((option) => {
  const country = countries.find(country => country.isoCode === option.value)
  return {
    dialingCode: country!.dialingCode,
    isoCode: country!.isoCode,
    name: option.label,
  }
})

withDefaults(defineProps<{
  disabled?: boolean
  hasErrors?: boolean
  pill?: boolean
  startWithNoCountryCode: boolean
}>(), { startWithNoCountryCode: false, })

const showCountryCode = ref<boolean>(!__props.startWithNoCountryCode)

const model = defineModel<Phone>()

// Normalize model value to Phone object with defaults
const normalizedPhoneNumber = _computed<Phone>(() => {
  return parsePhone(model.value)
})

const isPlusOne = _computed(() => normalizedPhoneNumber.value.countryDialingCode === defaultCountryCode)
const phoneMask = _computed(() => isPlusOne.value ? '(###) ###-####' : '#*')

const dialingCodeDisplay = _computed(() => {
  const dialingCode = `+${normalizedPhoneNumber.value.countryDialingCode}`
  return isPlusOne.value
    ? `${normalizedPhoneNumber.value.countryIsoCode} ${dialingCode}`
    : dialingCode
})

// Reveal the country code selector for a restored non-US number.
watch(() => normalizedPhoneNumber.value.countryIsoCode, (iso) => {
  if (iso && iso !== 'US') showCountryCode.value = true
}, { immediate: true })

let expanded = _ref(false)
function toggleExpansion () {
  if (expanded.value) {
    collapseDropdown()
  }
  else {
    expanded.value = true
    scrollFocusedItemIntoView()
  }
}

function collapseDropdown () {
  if (expanded.value) {
    expanded.value = false
    mainInput.value?.focus()
  }
}

const dropdownEl = _ref<HTMLElement>(undefined as any)
const el = (useCurrentElement<HTMLElement>())

onClickOutside((el), collapseDropdown)
useResizeObserver((dropdownEl), repositionDropdown)

// Calculates the position of the dropdown respective to the input.
async function repositionDropdown () {
  const { x, y } = await computePosition(el.value, dropdownEl.value, {
    middleware: [flip(), offset(8)],
  })
  Object.assign(dropdownEl.value.style, { top: `${y}px`, left: `${x}px` })
}

const filter = ref('')

const filteredCountries = _computed(() => {
  return allowedCountryCodes.filter((country) => {
    const dialCodeMatch = filter.value.match(/^\+?(\d{0,4})$/)
    return (
      (dialCodeMatch && country.dialingCode.toString().startsWith(dialCodeMatch[1]))
      || country.name.toLowerCase().startsWith(filter.value.toLowerCase())
    )
  })
})

function onCountryFilterKeyPress (value: KeyboardEvent) {
  if (value.key === 'Enter') {
    value.preventDefault()
    collapseDropdown()
  }
}

const emit = defineEmits(['update:modelValue', 'update:countryIsoCode'])

function updateCountry (isoCode: string) {
  const dialingCode = countries.find(c => c.isoCode === isoCode)?.dialingCode.toString() || defaultCountryCode
  emit('update:modelValue', {
    ...normalizedPhoneNumber.value,
    countryIsoCode: isoCode,
    countryDialingCode: dialingCode,
  })
}

function updatePhoneNumber (number: string) {
  // Treat input with no digits as empty
  const hasDigits = /\d/.test(number)
  emit('update:modelValue', {
    ...normalizedPhoneNumber.value,
    number: hasDigits ? number : '',
  })
}

function onCountryFilterKeyDown (value: KeyboardEvent) {
  if (value.key === 'ArrowDown' || value.key === 'ArrowUp') {
    value.preventDefault()
    const currentIndex = filteredCountries.value.findIndex(
      country => country.isoCode === normalizedPhoneNumber.value.countryIsoCode,
    )
    if (value.key === 'ArrowDown') {
      const nextIndex = (currentIndex + 1) % filteredCountries.value.length
      updateCountry(filteredCountries.value[nextIndex].isoCode)
    }
    else if (value.key === 'ArrowUp') {
      const prevIndex = (currentIndex - 1 + filteredCountries.value.length) % filteredCountries.value.length
      updateCountry(filteredCountries.value[prevIndex].isoCode)
    }
  }
}

const mainInput = useTemplateRef('mainInput')

function selectCountry (country: { isoCode: string; dialingCode: number; name: string }) {
  updateCountry(country.isoCode)
  collapseDropdown()
}

const countryFilterBox = useTemplateRef('countryFilter')

function scrollFocusedItemIntoView () {
  nextTick(() => {
    if (!dropdownEl.value) return

    const focusedElement = dropdownEl.value.querySelector<HTMLElement>('.selected')
    if (focusedElement) {
      scrollIntoView(focusedElement, { animate: false, offset: -70 })
    }
  })
}

function onTextInputClick (event: MouseEvent) {
  if (event.target === mainInput.value?.inputElement)
    collapseDropdown()
}

const countryCode = computed(() => normalizedPhoneNumber.value.countryIsoCode || 'US')

function onMainInputFocus () {
  collapseDropdown()
}

watch(countryCode, () => {
  scrollFocusedItemIntoView()
})

watch(countryFilterBox, (theBox) => {
  if (theBox && expanded.value) {
    theBox.focus()
  }
})

watch(filter, () => {
  if (filteredCountries.value.length >= 1) {
    updateCountry(filteredCountries.value[0].isoCode)
  }
})
</script>

<template>
  <GTextInput
    ref="mainInput"
    centered
    :disabled="disabled"
    :pill="pill"
    :mask="phoneMask"
    :modelValue="normalizedPhoneNumber.number"
    @update:modelValue="updatePhoneNumber"
    @click="onTextInputClick"
    @focus="onMainInputFocus"
  >
    <template #inner>
      <div v-if="showCountryCode" class="w-25">
        <div
          class="country-code-selector flex items-center justify-center hover:cursor-pointer"
          :class="{ expanded }"
          @click="toggleExpansion"
        >
          <div
            class="w-full border-none outline-none text-center font-medium"
            data-testid="dialing-code-display"
          >
            {{ dialingCodeDisplay }}
          </div>
          <div class="absolute inset-y-0 right-1 flex items-center p-2">
            <GIcon
              class="select-input-arrow pointer-events-none"
              :class="{ 'opacity-20': disabled, 'text-red-400': hasErrors }"
              style="top: 14px;"
              :icon="faChevronDown"
              size="xs"
            />
          </div>
        </div>
      </div>

      <div v-if="expanded" ref="dropdownEl" class="absolute right-0 bg-white country-list z-10">
        <div class="p-3 pb-0">
          <!-- Using a custom value in autocomplete as Chrome won't honor the autocomplete="off"-->
          <GTextInput
            ref="countryFilter"
            v-model="filter"
            wrapperClass="country-code-search-box"
            placeholder="Search by country or number"
            :pill="pill"
            autocomplete="phone-country-search"
            @keypress="onCountryFilterKeyPress"
            @keydown="onCountryFilterKeyDown"
          >
            <template #addonLeft>
              <GIcon
                :icon="faMagnifyingGlass"
                size="sm"
                class="text-v2-tertiary"
              />
            </template>
          </GTextInput>
        </div>
        <div class="overflow-y-auto mt-3 max-h-49 p-3 pt-0 mb-1 mr-2">
          <div
            v-for="country in filteredCountries"
            :key="country.isoCode"
            class="flex flex-row justify-between p-2 hover:cursor-pointer country font-medium"
            :class="{ selected: country.isoCode === normalizedPhoneNumber.countryIsoCode }"
            @click="() => selectCountry(country)"
          >
            <div>
              {{ country.name }}
            </div>
            <div class="text-v2-tertiary">
              +{{ country.dialingCode }}
            </div>
          </div>
        </div>
      </div>

      <GButton
        v-if="startWithNoCountryCode && !showCountryCode"
        class="absolute right-4"
        primary
        flat
        @click="showCountryCode = true"
      >
        Not in the US?
      </GButton>
    </template>
  </GTextInput>
</template>

<style scoped>
.country-code-selector {
  border-right: solid 1px var(--br);
  bottom: 0;
  left: 0;
  padding: 16px;
  position: absolute;

  /* This will cover the entire parent height including padding */
  top: 0;
  width: 100px;
}

.country-list {
  border: solid 1px var(--br);
  border-radius: 20px;
}

.country {
  &.selected {
    @apply bg-surface-blue;
  }

  &:hover {
    @apply bg-surface-blue;
  }
}

.select-input-arrow {
  transition: transform 0.1s ease-out;

  .expanded & {
    transform: translateY(2px) rotate(180deg);
  }
}

:deep(.g-inner-input) {
  flex-basis: 0;
  min-width: 0;
}
</style>
